import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { LogIn, ArrowRight, ShieldCheck, Store } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UnifiedLoginPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { login: setAdminAuth } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { timeout: 12000 },
      );

      if (data.role === 'admin') {
        setAdminAuth(data.access_token, data.user);
        toast({
          title: 'Bienvenue',
          description: 'Redirection vers le tableau de bord administrateur.',
        });
        navigate('/admin/dashboard');
        return;
      }

      if (data.role === 'partner') {
        localStorage.setItem('partner_token', data.access_token);
        localStorage.setItem('partner_user', JSON.stringify(data.user));
        toast({
          title: `Bienvenue ${data.user.garage_name}`,
          description: 'Redirection vers votre espace partenaire.',
        });
        navigate('/partenaire/calendrier');
        return;
      }

      toast({
        variant: 'destructive',
        title: 'Rôle inconnu',
        description: 'Ce compte n’a pas de rôle reconnu.',
      });
    } catch (err) {
      const status = err.response?.status;
      const serverDetail = err.response?.data?.detail;

      if (status === 401 || status === 403) {
        toast({
          variant: 'destructive',
          title: 'Identifiants incorrects',
          description: serverDetail || 'Login ou mot de passe incorrect. Vérifiez votre saisie.',
        });
      } else if (status === 422) {
        toast({
          variant: 'destructive',
          title: 'Données invalides',
          description: 'Vérifiez le format de l’email.',
        });
      } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        toast({
          variant: 'destructive',
          title: 'Connexion trop lente',
          description: 'Le serveur met trop de temps à répondre. Vérifiez votre connexion et réessayez.',
        });
      } else if (status >= 500 && status < 600) {
        toast({
          variant: 'destructive',
          title: 'Service indisponible',
          description: 'Le serveur ne répond pas correctement. Réessayez dans quelques instants.',
        });
      } else if (!err.response) {
        toast({
          variant: 'destructive',
          title: 'Erreur réseau',
          description: 'Impossible de joindre le serveur. Vérifiez votre connexion internet.',
        });
      } else {
        toast({
          variant: 'destructive',
          title: 'Erreur inattendue',
          description: serverDetail || 'Une erreur est survenue. Merci de réessayer.',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-0 shadow-2xl" data-testid="unified-login-card">
        <CardHeader className="text-center space-y-3">
          <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-[#52a675] to-[#3d8a5c] flex items-center justify-center">
            <LogIn className="text-white" size={26} />
          </div>
          <CardTitle className="text-2xl">Connexion</CardTitle>
          <CardDescription>
            Entrez vos identifiants. Vous serez redirigé automatiquement vers votre espace.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="vous@exemple.ch"
                data-testid="input-login-email"
              />
            </div>
            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="input-login-password"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-[#52a675] hover:bg-[#3d8a5c]"
              disabled={loading || !email || !password}
              data-testid="btn-login-submit"
            >
              {loading ? 'Connexion…' : (<>Se connecter <ArrowRight size={16} className="ml-2" /></>)}
            </Button>
          </form>

          <div className="mt-6 pt-4 border-t border-gray-100">
            <p className="text-[11px] text-gray-500 text-center mb-2">Espaces disponibles :</p>
            <div className="flex items-center justify-center gap-4 text-xs text-gray-600">
              <span className="flex items-center gap-1"><ShieldCheck size={13} className="text-[#52a675]" /> Administrateur</span>
              <span className="flex items-center gap-1"><Store size={13} className="text-amber-600" /> Partenaire Garage</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default UnifiedLoginPage;
